// Fælles alarm (spor "coop"): et lyn sigter efter én luftledning ved en by, og begge roller skal
// handle inden for varslets 12 sekunder. Driftsvagten forstærker masten; Planlæggeren opgraderer
// ledningen eller bygger en ledning mere til byen. Begge klaret: ledningen holder, og kassen får en
// bonus. Én klaret: ledningen bærer det halve, til en montør har set på den. Ingen: den knækker.
//
// Kun rum med to mennesker sætter `state.duo` (Volt solo gør ikke), så solo-hashen er uændret. Ingen
// tilfældighed: ledningen er den mest belastede, der kan opgraderes, og tiden er fast.

import { lineById, upgradeCost } from './grid';
import type { GridNode, Intent, Line, SimEvent, State, Threat } from './types';
import { TICK_HZ } from './types';

/** Lynet rammer 20 s inde i året, varslet står 12 s, og alarmen kommer i år 2 og 4 (indeks 1 og 3). */
export const DUO_AT_TICKS = 20 * TICK_HZ;
export const DUO_WARN_TICKS = 12 * TICK_HZ;
export const DUO_YEARS: readonly number[] = [1, 3];
/** Begge nåede det: så meget i kassen. */
export const DUO_BONUS = 4;

export type DuoRole = 'plan' | 'drift';

function liveCity(n: GridNode | undefined): n is GridNode {
  return !!n && n.kind === 'city' && n.active && !n.phantom && n.city !== undefined;
}

/** Byen ved ledningen, som alarmen handler om (den første levende by i enderne). */
export function duoCity(state: State, line: Line): GridNode | undefined {
  const a = state.nodes.find((n) => n.id === line.a);
  if (liveCity(a)) return a;
  const b = state.nodes.find((n) => n.id === line.b);
  return liveCity(b) ? b : undefined;
}

/** Ledningen, lynet vil sigte efter: en hel luftledning ved en by, som kan opgraderes. Mest belastet først. */
export function duoLine(state: State): Line | undefined {
  let best: Line | undefined;
  for (const l of state.lines) {
    if (l.medium !== 'air' || l.status !== 'ok' || l.halfDown || l.reinforced) continue;
    if (upgradeCost(state, l) === null) continue;
    if (!duoCity(state, l)) continue;
    if (state.threats.some((t) => t.phase !== 'done' && t.targetLine === l.id)) continue;
    if (!best || l.loadPm > best.loadPm || (l.loadPm === best.loadPm && l.id < best.id)) best = l;
  }
  return best;
}

/** Den fælles alarm i varslet, hvis der er en. */
export function liveDuo(state: State): Threat | undefined {
  return state.threats.find((t) => t.kind === 'duo' && t.phase === 'warning' && t.duo !== undefined);
}

/** Venter alarmen stadig på netop denne rolles handling på ledningen? Så er handlingen gratis. */
export function duoWants(state: State, lineId: string, role: DuoRole): boolean {
  const t = liveDuo(state);
  return !!t && t.targetLine === lineId && !t.duo?.[role];
}

/** Efter en udført handling: tæller den med i alarmen? */
export function creditDuo(state: State, intent: Intent, events: SimEvent[]): void {
  const t = liveDuo(state);
  if (!t?.duo) return;
  const d = t.duo;
  const line = lineById(state, t.targetLine);
  let role: DuoRole | null = null;
  if (!d.drift && intent.kind === 'reinforce_mast' && line?.reinforced) role = 'drift';
  else if (!d.plan && intent.kind === 'upgrade_line' && intent.target === t.targetLine) role = 'plan';
  else if (!d.plan && intent.kind === 'build_line' && (intent.target ?? '').split('|').includes(t.nodes?.[0] ?? '\u0000')) role = 'plan';
  if (!role) return;
  d[role] = true;
  events.push({ t: 'duo_step', tick: state.tick, threat: t.id, role, by: intent.player });
}
